import { ActionIcon, Box, Group, ScrollArea, Stack, Text } from '@mantine/core';
import { IconArrowLeft, IconX } from '@tabler/icons';
import React from 'react';
import { Message } from '../../../models/chat';
import { ChatInput } from './ChatInput';
import { MessageSkeleton, PaginationSkeleton } from './Skeletons';

interface RightPaneProps {
  activeChatTitle: string;
  isChatActive: boolean;
  isMobile: boolean;
  isDragging: boolean;
  loadingMessages: boolean;
  loadingOlder: boolean;
  messages: Message[];
  renderedMessages: React.ReactNode[];
  selectedUserName?: string;
  viewport: React.RefObject<HTMLDivElement>;
  onScrollPositionChange: (pos: { x: number; y: number }) => void;
  onSend: (content: string) => Promise<void>;
  onSendAttachment: (file: File) => Promise<void>;
  onClose: () => void;
  onBack: () => void;
  onMouseDown: (e: React.MouseEvent) => void;
}

export const RightPane = React.memo(function RightPane({
  activeChatTitle,
  isChatActive,
  isMobile,
  isDragging,
  loadingMessages,
  loadingOlder,
  messages,
  renderedMessages,
  selectedUserName,
  viewport,
  onScrollPositionChange,
  onSend,
  onSendAttachment,
  onClose,
  onBack,
  onMouseDown,
}: RightPaneProps) {
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      {/* Chat header (draggable handle) */}
      <Box
        p="md"
        onMouseDown={onMouseDown}
        sx={{
          borderBottom: '1px solid #ccc',
          backgroundColor: '#f8f9fa',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
        }}
      >
        <Group position="apart">
          <Group spacing="xs">
            {isMobile && (
              <ActionIcon size="sm" onClick={onBack}>
                <IconArrowLeft size={16} />
              </ActionIcon>
            )}
            <Text
              weight={700}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {activeChatTitle}
            </Text>
          </Group>
          <ActionIcon onClick={onClose}>
            <IconX size={16} />
          </ActionIcon>
        </Group>
      </Box>

      {/* Message area or placeholder */}
      {isChatActive ? (
        <>
          <ScrollArea
            sx={{ flex: 1 }}
            viewportRef={viewport}
            p="md"
            onScrollPositionChange={onScrollPositionChange}
          >
            <Stack spacing="xs">
              {messages.length === 0 && loadingMessages ? (
                <MessageSkeleton />
              ) : (
                <>
                  {loadingOlder && <PaginationSkeleton />}
                  {renderedMessages}
                  {messages.length === 0 && !loadingMessages && (
                    <Text size="sm" color="dimmed" align="center" mt="xl">
                      {selectedUserName
                        ? `Envie uma mensagem para iniciar a conversa com ${selectedUserName}`
                        : 'Nenhuma mensagem encontrada'}
                    </Text>
                  )}
                </>
              )}
            </Stack>
          </ScrollArea>
          <Box
            p="md"
            sx={{ borderTop: '1px solid #ccc', backgroundColor: '#fff' }}
          >
            <ChatInput onSend={onSend} onSendAttachment={onSendAttachment} />
          </Box>
        </>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f0f2f5',
          }}
        >
          <Text color="dimmed" align="center">
            Selecione uma conversa na esquerda ou inicie uma nova.
          </Text>
        </Box>
      )}
    </Box>
  );
});
